import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  Legend,
} from 'recharts';

/**
 * Line chart component for displaying messages over time
 * with separate lines for external and internal users
 */

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-sm rounded-md">
        <p className="text-sm font-semibold">{formatDateForDisplay(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value} messages
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const LineChartPlaceholder = ({ 
  title = "Message Trends Over Time",
  subtitle = "",
  data = [],
  categories = ["External (English)", "External (Spanish)", "Internal"],
  colors = ["#10B981", "#F59E0B", "#3B82F6"], // Green for English, Orange for Spanish, Blue for Internal
  accountTypeFilter = null
}) => {
  // Default parameters for axes
  const xAxisProps = {
    dataKey: "date",
    tick: { fontSize: 12 },
    height: 60,
    scale: "auto",
    padding: { left: 0, right: 0 }
  };

  const yAxisProps = {
    allowDecimals: false,
    tick: { fontSize: 12 },
    width: 60,
    scale: "auto",
    padding: { top: 20, bottom: 0 }
  };

  return (
    <Card className="p-6 flex-1">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        {/* Legend */}
        <div className="flex items-center space-x-6 mb-4">
          {accountTypeFilter ? (
            <div className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded-sm"
                style={{ 
                  backgroundColor: 
                    accountTypeFilter === 'External (English)' ? '#10B981' :
                    accountTypeFilter === 'External (Spanish)' ? '#F59E0B' :
                    accountTypeFilter === 'Internal' ? '#3B82F6' : colors[0]
                }}
              ></div>
              <span className="text-sm text-gray-600">{accountTypeFilter}</span>
            </div>
          ) : (
            categories.map((category, index) => (
              <div key={category} className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-sm"
                  style={{ backgroundColor: colors[index] }}
                ></div>
                <span className="text-sm text-gray-600">{category}</span>
              </div>
            ))
          )}
        </div>

        {/* Chart */}
        {data && data.length > 0 ? (
          <div className="flex-1 flex flex-col">
            <div className="h-[400px] flex-grow">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis {...xAxisProps} />
                  <YAxis {...yAxisProps} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={36} />
                  {(!accountTypeFilter || accountTypeFilter === 'External (English)') && (
                    <Line 
                      type="monotone" 
                      dataKey="External (English)" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                      name="External (English)" 
                    />
                  )}
                  {(!accountTypeFilter || accountTypeFilter === 'External (Spanish)') && (
                    <Line 
                      type="monotone" 
                      dataKey="External (Spanish)" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      dot={{ fill: "#F59E0B", strokeWidth: 2, r: 4 }}
                      name="External (Spanish)" 
                    />
                  )}
                  {(!accountTypeFilter || accountTypeFilter === 'Internal') && (
                    <Line 
                      type="monotone" 
                      dataKey="Internal" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                      name="Internal" 
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-200 mb-4">
            <div className="text-center">
              <p className="text-gray-500 text-sm">No data available for the selected date range</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LineChartPlaceholder; 